// ReservePanel — ONE card, ONE Save, for the bounded reserve (contract §11 Stage 3.5).
//
// WHAT THE RESERVE IS, in the one sentence this card exists to make obvious: instead of chasing its
// profit target inside a 3-hour window — forgiving whatever it missed when the window rolls — a game
// carries ONE running figure of how far ahead or behind its own promise it is, forever. That figure
// is R. Nothing resets it, so nothing is forgiven and nothing has to be forced at the last minute,
// which is what makes the advertised top prize reachable on the very first round.
//
// OWNER RULE (memory feedback_consolidate_dont_scatter): simplifying means merging by real category
// into ONE card with ONE Save — never scattering. Everything that decides how the reserve behaves
// lives here: which game you are looking at, where it stands, how hard it steers, how far it may run
// behind, and the house bankroll every game's payout capacity is funded from. One Save writes the
// lot.
//
// HOUSE RULE, and the reason parts of this card can render read-only: a control that does nothing
// must never be rendered as if it does. Two things on this page are NOT controls here on purpose:
//   • `reserve_enabled` — the per-game rollout flag. It is a live manifest key and its ONE home is
//     that game's own settings page (Settings → Games → <game> → Target Profit). Duplicating it
//     here would be two switches for one flag, which is the scatter the owner rejected. The card
//     reads it and links to it.
//   • Everything else, whenever the admin API has no reserve endpoint deployed: the card says so in
//     plain words, names the endpoint, and disables every input rather than showing a dead Save.
//
// ════ BACKEND CONTRACT — what this card needs, and what does not exist yet ═══════════════════
// The endpoint is NOT in this agent's scope. Written down here in full so the agent who lands it has
// no decisions left to make; until it exists the card renders read-only and says so.
//
//   GET  /api/admin/platform/reserve        (requireAdmin; read open to any admin-side principal)
//     → ReserveGet, below. One entry per `game_reserve` row (game, session_type='pvh'), joined with
//       that game's `<game>_config.reserve_enabled` and `target_profit_pct`.
//       `giveBackPct` = `reserve.js giveBackRate({…row, sigma})` × 100, computed with the SAME law
//       the engines use — never re-derived in this file (§4.3: the law lives once).
//       `chainSharePct` = the turnover-weighted share of house P&L the agent chain carries, so the
//       card can state R_lim net of it. Absent is allowed; the card then says it cannot split it.
//       `bankroll` = `platform_config['house_bankroll']` plus `set:false` when the key is ABSENT
//       (that is the ₹50,000 placeholder case houseSolvency.js warns about), and the capacity
//       `readHouseCapacity()` would return today.
//
//   PUT  /api/admin/platform/reserve        (requireAdminRole — admin only, never an agent)
//     body { game, authorityZ, deadZoneZ?, bankroll?: { rupees } }, any subset; ONE audit row each.
//     THREE things it must do that are easy to miss:
//     1. RE-SIZE THE BOUND. §2.4 is B = 4.5·σ_ref·s̄/a_z, so a new authority is a new B:
//        B_new = round(B_old · a_z_old / a_z_new), and limit_paise = max(3·B_new, 1.2·maxPayout)
//        — the 1.2x floor is amendment A4 and is not optional. Writing `authority_bp` alone leaves
//        SD(R) = B/3 false, which is the closure the whole design rests on. The card shows the
//        resized B on every chip, so a save that does not resize makes the card a liar.
//     2. STAMP THE ANCHOR. `house_bankroll` is `{rupees, anchor_admin_wallet}` and the anchor must
//        be read SERVER-side from `admin_wallet.balance_points` at the moment of the save — "what
//        the P&L wallet read when I said this". The owner cannot be asked to copy it by hand, and a
//        stale anchor silently mis-states capacity.
//     3. VALIDATE. authorityZ ∈ [0.02, 1.0] (§3.2's manifest bounds), rupees ≥ 0 and finite.
//        Reject, do not clamp: a slider that silently rounds is the defect A6 exists to name.
//     It must NOT touch `reserve_paise` or any accumulator, and must NOT write `reserve_enabled` —
//     that flag's one home is the game's own settings page.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import ExplainedSetting from '@baaligames/shared-ui/admin/components/ExplainedSetting';
import { getGame } from '@baaligames/shared-ui/games/registry';
import {
  RESERVE_GAME_SPECS, RESERVE_GAME_ORDER, RESERVE_PRESETS, DEFAULT_AUTHORITY_Z,
  AUTHORITY_MIN, AUTHORITY_MAX, sizingAt, presetConsequence, customConsequence,
  bankrollVerdictFor,
  type ReservePresetKey,
} from '../../components/reservePresets';

/* ── the shape of GET /admin/platform/reserve ─────────────────────────────────────────────────── */

export type ReserveRow = {
  game: string;
  sessionType?: string;
  /** <game>_config.reserve_enabled — read only here; its control is the game's own settings page */
  enabled?: boolean | null;
  /** the game's configured target_profit_pct, so the card can check it against the A6 ceiling */
  targetPct?: number | null;
  reservePaise?: number | null;
  boundPaise?: number | null;
  limitPaise?: number | null;
  authorityBp?: number | null;
  deadZoneBp?: number | null;
  turnoverPaise?: number | null;
  profitPaise?: number | null;
  targetLinePaise?: number | null;
  forgivenPaise?: number | null;
  rounds?: number | null;
  promoBudgetPaise?: number | null;
  promoSpentPaise?: number | null;
  openedAt?: string | null;
  /** g, the current give-back rate, in take-POINTS (e.g. 0.42 = the aim sits 0.42 points below T).
   *  It is NOT computed here on purpose. g = a_z·σ_book·clamp(R/B) needs σ_book, which is a property
   *  of the board a round is actually played on, and §4.3 is explicit that this platform has twice
   *  shipped a second copy of a picker's own arithmetic and had it drift. The law lives in
   *  `_shared/reserve.js`; the endpoint calls `giveBackRate` and this card PRINTS what it is told.
   *  Absent ⇒ the card shows the position's fill instead and says the steer is unavailable. */
  giveBackPct?: number | null;
  /** optional server-measured A6 ceiling; falls back to the measured spec in reservePresets.ts */
  takeCeilingPct?: number | null;
};

export type ReserveGet = {
  games?: ReserveRow[] | null;
  bankroll?: {
    rupees?: number | null;
    anchorAdminWallet?: number | null;
    defaultRupees?: number | null;
    /** false ⇒ the key is absent and capacity is running on the placeholder */
    set?: boolean | null;
    adminWalletPoints?: number | null;
    capacityRupees?: number | null;
  } | null;
  /** turnover-weighted share of house P&L the agent chain carries (e.g. 54.45) */
  chainSharePct?: number | null;
};

/* ── formatting ───────────────────────────────────────────────────────────────────────────────── */

const inr = (r: number, dp = 0) =>
  `₹${Number(r).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const num = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const pctStr = (n: number, dp = 2) => `${n >= 0 ? '' : '−'}${Math.abs(n).toFixed(dp)}%`;
const ptStr = (n: number, dp = 2) => `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(dp)} pt`;
const gameLabel = (id: string) => getGame(id)?.displayName ?? id;

/* ── the readout tile ─────────────────────────────────────────────────────────────────────────── */

function Tile({ label, value, sub, tone = 'plain' }: {
  label: string; value: string; sub?: string; tone?: 'plain' | 'good' | 'warn' | 'bad';
}) {
  const ring = tone === 'bad'
    ? 'ring-rose-300/70 dark:ring-rose-400/30 bg-rose-50 dark:bg-rose-500/10'
    : tone === 'warn'
      ? 'ring-amber-300/70 dark:ring-amber-400/30 bg-amber-50 dark:bg-amber-500/10'
      : tone === 'good'
        ? 'ring-emerald-300/70 dark:ring-emerald-400/30 bg-emerald-50 dark:bg-emerald-500/10'
        : 'ring-arena-200/60 dark:ring-arena-600/40 bg-white dark:bg-arena-800/40';
  return (
    <div className={`rounded-xl ring-1 ring-inset px-3 py-2.5 ${ring}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-arena-500 dark:text-neon-200/60">{label}</div>
      <div className="mt-0.5 text-[15px] font-bold text-arena-900 dark:text-white break-words">{value}</div>
      {sub && <div className="mt-0.5 text-[11.5px] leading-snug text-arena-500 dark:text-neon-200/65">{sub}</div>}
    </div>
  );
}

/* ── the card ─────────────────────────────────────────────────────────────────────────────────── */

export type ReserveState = {
  data: ReserveGet | null;
  loading: boolean;
  loadErr: string;
  reload: () => Promise<void>;
};

/** The GET, in a hook — the same shape AdrenalinePanel uses, so the hub can lift it later without
 *  this file changing. Exported so the card can be rendered (and reviewed) against a KNOWN state
 *  instead of only against whatever the server happens to answer. */
export function useReserveState(enabled = true): ReserveState {
  const [data, setData] = useState<ReserveGet | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [loadErr, setLoadErr] = useState('');

  const reload = useCallback(async () => {
    setLoading(true); setLoadErr('');
    try {
      const d = await adminApi.get<ReserveGet>('/admin/platform/reserve');
      setData(d ?? null);
    } catch (e) {
      setData(null);
      setLoadErr(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not load the reserve.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (enabled) void reload(); }, [enabled, reload]);

  return { data, loading, loadErr, reload };
}

export default function ReservePanel({ state }: { state?: ReserveState } = {}) {
  const own = useReserveState(!state);
  const { data, loading, loadErr, reload: load } = state ?? own;
  const [game, setGame] = useState<string>('plinko');
  const [az, setAz] = useState<number | string>(DEFAULT_AUTHORITY_Z);
  const [bankroll, setBankroll] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // One row per game. The table is keyed (game, session_type) and only 'pvh' is seeded — if a second
  // session type ever appears, the house-banked row is the one this card is about, so it wins rather
  // than whichever happens to come last in the response.
  const rows = useMemo(() => {
    const m = new Map<string, ReserveRow>();
    for (const r of data?.games ?? []) {
      if (!r || typeof r.game !== 'string') continue;
      const prev = m.get(r.game);
      if (prev && prev.sessionType === 'pvh' && r.sessionType !== 'pvh') continue;
      m.set(r.game, r);
    }
    return m;
  }, [data]);

  const spec = RESERVE_GAME_SPECS[game];
  const row = rows.get(game) ?? null;
  // Every input is dead until the admin API actually answers. Saying so is the point.
  const wired = !!data && Array.isArray(data.games);

  // Seed the authority from the row whenever the game changes or the GET lands.
  useEffect(() => {
    const bp = num(rows.get(game)?.authorityBp);
    setAz(bp != null && bp > 0 ? bp / 10000 : DEFAULT_AUTHORITY_Z);
  }, [game, rows]);

  useEffect(() => {
    const r = num(data?.bankroll?.rupees);
    setBankroll(r != null ? String(r) : '');
  }, [data]);

  const azNum = typeof az === 'number' ? az : Number(az);
  const azValid = Number.isFinite(azNum) && azNum >= AUTHORITY_MIN && azNum <= AUTHORITY_MAX;

  /* ── derived, all exact, all from the row ────────────────────────────────────────────────────
   * §3.4 closes `profit ≡ target_line + reserve + forgiven` to the paisa, so:
   *   realised take   = profit / turnover                         (what the house actually kept)
   *   dialled average = target_line / turnover        (the average T over the life of the reserve)
   *   difference      = (profit − target_line) / turnover = (R + forgiven) / turnover
   * Contract §11 Stage 3.5 calls the THIRD of those "the long-run realised take". It is not — it is
   * the DEVIATION from the target line; the take is the first. All three are shown, because the
   * owner needs the take and the contract needs its identity, and they are one subtraction apart. */
  const live = useMemo(() => {
    if (!row) return null;
    const R = num(row.reservePaise) ?? 0;
    const B = num(row.boundPaise) ?? 0;
    const L = num(row.limitPaise) ?? 0;
    const turnover = num(row.turnoverPaise) ?? 0;
    const profit = num(row.profitPaise) ?? 0;
    const line = num(row.targetLinePaise) ?? 0;
    const forgiven = num(row.forgivenPaise) ?? 0;
    return {
      R, B, L, turnover, profit, line, forgiven,
      rounds: num(row.rounds) ?? 0,
      pctOfBound: B > 0 ? (R / B) * 100 : null,
      takePct: turnover > 0 ? (profit / turnover) * 100 : null,
      dialledPct: turnover > 0 ? (line / turnover) * 100 : null,
      deviationPt: turnover > 0 ? ((profit - line) / turnover) * 100 : null,
      promoBudget: num(row.promoBudgetPaise) ?? 0,
      promoSpent: num(row.promoSpentPaise) ?? 0,
      giveBackPct: num(row.giveBackPct),
      fillPct: B > 0 ? Math.max(-100, Math.min(100, (R / B) * 100)) : null,
      // §3.4, checked here rather than trusted: profit ≡ target_line + reserve + forgiven, to the
      // paisa. It is four numbers off one row and one subtraction, so the card can tell the owner
      // the account balances instead of assuming it — and a non-zero residual means the row is
      // telling a story it cannot back up, which is worth a loud tile.
      identityResidualPaise: profit - (line + R + forgiven),
    };
  }, [row]);

  // The authority the row actually holds (null when there is no row to read).
  const storedAz = useMemo(() => {
    const bp = num(row?.authorityBp);
    return bp != null && bp > 0 ? bp / 10000 : null;
  }, [row]);
  const azUnchanged = storedAz != null && azValid && Math.abs(azNum - storedAz) < 1e-9;

  // R_lim in rupees. While the chosen authority still matches the row, the LIVE limit is the truth —
  // an operator may have re-sized the row by hand and the card must not overwrite that with its own
  // arithmetic. The moment the authority is changed, the figure shown is what the save will produce.
  const pending = spec && azValid ? sizingAt(spec, azNum) : null;
  const chainSharePct = num(data?.chainSharePct);
  const limitRupees = azUnchanged && live && live.L > 0
    ? live.L / 100
    : (pending?.limitRupees ?? (live && live.L > 0 ? live.L / 100 : null));
  const adminCarries = limitRupees != null && chainSharePct != null
    ? limitRupees * (1 - chainSharePct / 100) : null;

  const ceiling = num(row?.takeCeilingPct) ?? spec?.takeCeilingPct ?? null;
  const targetPct = num(row?.targetPct);
  const ceilingBreached = ceiling != null && targetPct != null && targetPct > ceiling;

  // "since the reserve opened" is a real date on the row, not a figure of speech.
  const openedLabel = useMemo(() => {
    const raw = row?.openedAt;
    if (!raw) return '';
    const d = new Date(String(raw).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }, [row]);

  const bankrollSet = data?.bankroll?.set === true && num(data?.bankroll?.rupees) != null;
  const bankrollDefault = num(data?.bankroll?.defaultRupees) ?? 50000;

  const presets = useMemo(() => {
    if (!spec) return [];
    return RESERVE_PRESETS.map((p) => ({
      value: p.az,
      label: p.label,
      tagline: `${p.az.toFixed(2)} · B ${inr(sizingAt(spec, p.az).boundRupees)}`,
      tone: p.tone,
      consequence: presetConsequence(spec, p.key as ReservePresetKey),
    }));
  }, [spec]);

  const dirty = useMemo(() => {
    const azChanged = azValid && storedAz != null && Math.abs(azNum - storedAz) > 1e-9;
    const storedBank = num(data?.bankroll?.rupees);
    const typed = bankroll.trim() === '' ? null : Number(bankroll);
    const bankChanged = typed != null && Number.isFinite(typed) && typed !== storedBank;
    return { azChanged, bankChanged, any: azChanged || bankChanged };
  }, [storedAz, azNum, azValid, bankroll, data]);

  async function save() {
    if (!wired || !spec || !dirty.any) return;
    setMsg(''); setErr('');
    const lines: string[] = [];
    if (dirty.azChanged) {
      const s = sizingAt(spec, azNum);
      lines.push(`${gameLabel(game)} steers at authority ${azNum} — working scale ${inr(s.boundRupees)}, `
        + `and it may run up to ${inr(s.limitRupees)} below its target line.`);
    }
    if (dirty.bankChanged) {
      lines.push(`House bankroll set to ${inr(Number(bankroll))} — the most you are willing to have on the table across ALL games at once.`);
    }
    if (!window.confirm(`Save these reserve settings?\n\n${lines.join('\n\n')}`)) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { game };
      if (dirty.azChanged) body.authorityZ = azNum;
      if (dirty.bankChanged) body.bankroll = { rupees: Number(bankroll) };
      await adminApi.put('/admin/platform/reserve', body);
      await load();
      setMsg('Saved.');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed.');
    } finally { setSaving(false); }
  }

  const enabledGames = (data?.games ?? []).filter((r) => r?.enabled === true).map((r) => gameLabel(r.game));

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-arena-900 dark:text-white flex items-center gap-2">
          <Icon name="savings" size={20} /> Reserve
        </h1>
        <p className="text-[13px] text-arena-500 dark:text-neon-200/70">
          How each game DELIVERS the profit target you set — not what the target is. Instead of
          keeping score in 3-hour windows and forgetting the difference when the window rolls, a game
          carries one running figure of how far ahead or behind its own promise it is. It never
          resets, so nothing is forgiven and nothing has to be forced at the last minute — which is
          what makes the advertised top prize reachable on the very first round.
        </p>
        <p className="text-[12px] font-semibold text-arena-600 dark:text-neon-200/80">
          Now active:{' '}
          <span className="text-violet-600 dark:text-violet-300">
            {loading && !data
              ? '…'
              : !wired
                ? 'unknown — this page cannot read the reserve yet'
                : enabledGames.length ? `${enabledGames.join(', ')}` : 'off on every game'}
          </span>
        </p>
      </header>

      {/* Not wired — the honest state. Everything below renders read-only. */}
      {!loading && !wired && (
        <div className="rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed
                        bg-amber-50 border-amber-300/70 text-amber-900
                        dark:bg-amber-500/10 dark:border-amber-400/30 dark:text-amber-100">
          <div className="font-bold flex items-center gap-1.5">
            <Icon name="warning" size={16} /> Nothing on this card can be changed yet.
          </div>
          <p className="mt-1">
            The admin API has no reserve endpoint deployed, so this page cannot read where any game
            stands and cannot save. It needs <code className="font-mono">GET/PUT /api/admin/platform/reserve</code> over the
            {' '}<code className="font-mono">game_reserve</code> table. Until it exists, the explanations below are still
            correct — they come from the sizing measurement committed with migration 168 — but every
            control is disabled, because a control that does nothing must never look like one that does.
          </p>
          {loadErr && <p className="mt-1 opacity-80">Server said: {loadErr}</p>}
          <button onClick={() => void load()} className="mt-1.5 text-[12px] font-bold underline">Retry</button>
        </div>
      )}

      {/* ── 1. which game ───────────────────────────────────────────────────────────────────── */}
      <div>
        <div className="text-[12px] font-bold text-arena-700 dark:text-neon-200/80 mb-1.5">Game</div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {RESERVE_GAME_ORDER.filter((g) => RESERVE_GAME_SPECS[g]).map((g) => {
            const on = g === game;
            const r = rows.get(g);
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGame(g)}
                aria-pressed={on}
                className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-bold transition active:scale-[.98]
                            ${on ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.35)]'
                                 : 'bg-white/70 dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/75 border border-arena-200/60 dark:border-arena-600/40 hover:border-violet-400/50'}`}
              >
                {gameLabel(g)}
                {r?.enabled === true && <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-white' : 'bg-emerald-500'}`} title="reserve on" />}
                {num(r?.forgivenPaise) ? <Icon name="error" size={14} className={on ? 'text-white' : 'text-rose-500'} /> : null}
              </button>
            );
          })}
        </div>
      </div>

      {!spec ? null : (
        <>
          {/* ── 2. where this game stands ─────────────────────────────────────────────────── */}
          <section className="rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-4 sm:p-5 space-y-3">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <h2 className="text-[14px] font-bold text-arena-900 dark:text-white">
                Where {gameLabel(game)} stands
              </h2>
              <span className="text-[11px] font-semibold text-arena-500 dark:text-neon-200/70">
                Reserve is{' '}
                <span className={row?.enabled === true ? 'text-emerald-600 dark:text-emerald-300' : 'text-arena-700 dark:text-neon-100'}>
                  {row?.enabled === true ? 'ON' : 'off'}
                </span>{' '}
                for this game
              </span>
            </div>

            {!live || live.rounds === 0 ? (
              <p className="text-[12.5px] text-arena-500 dark:text-neon-200/65">
                {!wired
                  ? 'No live figures: the reserve endpoint is not deployed, so this card cannot read the position. Turn nothing on until it can.'
                  : !row
                    ? 'This game has no reserve row at all, so nothing could be counted even with the switch on. One row per house-banked game is created by the migration that opened the reserve — get that sorted before turning anything on here.'
                    : (live && live.B === 0)
                      ? 'This game’s row exists but was never sized — with no working scale the reserve counts nothing and steers nothing. It must be sized, with the measurement, before the switch means anything.'
                      : 'Nothing counted yet — no round has been played with the reserve on, so there is no position to show. The figures below describe what it WILL do.'}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Tile
                    label="Position (R)"
                    value={`${live.R >= 0 ? '' : '−'}${inr(Math.abs(live.R) / 100)}`}
                    sub={live.R >= 0
                      ? `ahead of its promise${live.pctOfBound != null ? ` · ${live.pctOfBound.toFixed(0)}% of its working scale` : ''}`
                      : `behind its promise${live.pctOfBound != null ? ` · ${Math.abs(live.pctOfBound).toFixed(0)}% of its working scale` : ''}`}
                    tone={live.pctOfBound != null && Math.abs(live.pctOfBound) > 300 ? 'warn' : 'plain'}
                  />
                  <Tile
                    label="Realised take"
                    value={live.takePct != null ? pctStr(live.takePct) : '—'}
                    sub={live.dialledPct != null && live.deviationPt != null
                      ? `dialled ${pctStr(live.dialledPct)} · ${ptStr(live.deviationPt)} from the line`
                      : 'since the reserve opened'}
                  />
                  <Tile
                    label="Steering now"
                    value={live.giveBackPct != null
                      ? `${live.giveBackPct >= 0 ? '−' : '+'}${Math.abs(live.giveBackPct).toFixed(2)} pt`
                      : live.fillPct != null ? `${live.fillPct.toFixed(0)}% of scale` : '—'}
                    sub={live.giveBackPct != null
                      ? (live.giveBackPct >= 0
                        ? 'the game is aiming that far BELOW its target to hand the excess back'
                        : 'the game is aiming that far ABOVE its target to make the shortfall up')
                      : 'how full the position is; the exact steer differs per board and is reported by the engine, not guessed here'}
                  />
                  <Tile
                    label="Counted play"
                    value={inr(live.turnover / 100)}
                    sub={`${live.rounds.toLocaleString('en-IN')} rounds${openedLabel ? ` since ${openedLabel}` : ''} · real players only (a test account never moves any of these figures)`}
                  />
                  <Tile
                    label="Forgiven"
                    value={live.forgiven === 0 ? '₹0' : `${inr(live.forgiven / 100)}`}
                    sub={live.forgiven === 0
                      ? 'as it must be — every paisa is accounted for'
                      : 'ALARM: money was clipped out of the account. This is not a statistic; find out why.'}
                    tone={live.forgiven === 0 ? 'good' : 'bad'}
                  />
                </div>
                {live.identityResidualPaise !== 0 && (
                  <div className="rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed
                                  bg-rose-50 border-rose-300/70 text-rose-900
                                  dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-100">
                    <b>The account does not balance.</b> What this game kept should equal what it
                    promised, plus how far ahead it is, plus anything forgiven — and it is out by{' '}
                    {inr(Math.abs(live.identityResidualPaise) / 100, 2)}. That is not a rounding
                    difference (the whole account is kept in paise precisely so it cannot be one); it
                    means a round moved money without being counted. Do not turn anything else on
                    until it is explained.
                  </div>
                )}
                {live.promoBudget > 0 && (
                  <p className="text-[12px] text-arena-600 dark:text-neon-200/70">
                    Give-back promo: {inr(live.promoSpent / 100)} spent of {inr(live.promoBudget / 100)} authorised
                    {live.promoSpent >= live.promoBudget && ' — EXHAUSTED. The target snaps to 0% for new rounds; close it out so the engine stops working the residue back off players.'}
                  </p>
                )}
                <p className="text-[11.5px] text-arena-500 dark:text-neon-200/60">
                  Take is profit ÷ play and the dial is the target line ÷ play — both exact, both
                  straight off this game&rsquo;s own counters, neither estimated or sampled.
                </p>
              </>
            )}

            {row?.enabled !== true && (
              <p className="text-[12px] text-arena-500 dark:text-neon-200/65">
                Turning the reserve on for a game is one switch, and it lives with that game&rsquo;s other
                money settings, not here:{' '}
                <Link to={`/settings/games/${game}`} className="font-bold underline text-violet-600 dark:text-violet-300">
                  {gameLabel(game)} settings → Target Profit
                </Link>. Set the house bankroll below FIRST.
              </p>
            )}
          </section>

          {/* ── 3. how hard it steers ─────────────────────────────────────────────────────── */}
          <ExplainedSetting
            title={`How hard ${gameLabel(game)} corrects itself`}
            help={`Everything the reserve decides comes from this one number. It is measured in the game's own swing, so 0.15 means the same thing on every board on the platform. Higher corrects the drift faster and leaves a bigger clue for a player counting losses; lower drifts further before it pulls back. Measured on ${spec.board} at ₹${spec.stakeRupees}.`}
            value={azValid ? azNum : az}
            onChange={(v) => setAz(v === '' ? '' : Number(v))}
            formatValue={(v) => {
              if (v === '' || v == null) return '—';
              const n = Number(v);
              if (!Number.isFinite(n)) return '—';
              const p = RESERVE_PRESETS.find((x) => Math.abs(x.az - n) < 1e-9);
              return p ? `${p.label} (${n})` : `Custom (${n})`;
            }}
            presets={presets}
            custom={{
              kind: 'number', min: AUTHORITY_MIN, max: AUTHORITY_MAX, step: 0.01,
              placeholder: 'e.g. 0.15',
              describe: (v: unknown) => customConsequence(spec, Number(v)),
            }}
            disabled={!wired || saving}
          />

          {/* Where the rupee figures come from, and how much to trust them. The sizing migration is
              blunt about this and so is the card: σ is measured off the engine and is solid; the
              STAKE it is scaled by is a guess on six of the eight games, because they have never
              been played for real money. B scales linearly in the stake, so a 10x stake error is a
              10x error in every rupee figure above. */}
          <p className="text-[11.5px] leading-snug text-arena-500 dark:text-neon-200/60 -mt-2 px-1">
            Those rupee figures assume a typical stake of ₹{Math.round(spec.sBar).toLocaleString('en-IN')} on{' '}
            {gameLabel(game)}.{' '}
            {spec.confidence === 'THIN'
              ? 'That comes from the handful of real bets this game has taken — thin evidence, and worth re-measuring once it has a few hundred.'
              : spec.confidence === 'CONFIG*'
                ? 'That is the configured default; the game’s own table suggests players actually stake far less, which would make the working scale smaller. Re-measure once it has real volume.'
                : 'Nobody has played this game for real money yet, so that is the configured default rather than an observed figure. The working scale moves with the real stake, so re-measure once it has volume.'}{' '}
            The swing it is multiplied by ({spec.sigmaRef.toFixed(2)}, on {spec.board}) is measured off the engine itself and does not depend on players.
          </p>

          {/* ── 4. how far behind it may run, in plain words + net of the chain ───────────── */}
          {pending && (
            <section className="rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-4 sm:p-5 space-y-2">
              <h2 className="text-[14px] font-bold text-arena-900 dark:text-white">How far behind {gameLabel(game)} may run</h2>
              <p className="text-[13px] leading-relaxed text-arena-700 dark:text-neon-100">
                This game may run up to <b>{inr(pending.limitRupees)}</b> below its target line; that
                is what makes the top prize reachable. It is not a promise to lose that much — in
                normal play the position sits near zero — it is the furthest behind it is ever allowed
                to be at one moment.
              </p>
              <p className="text-[12.5px] leading-relaxed text-arena-600 dark:text-neon-200/75">
                {adminCarries != null ? (
                  <>
                    You do not carry all of it. At the {chainSharePct!.toFixed(2)}% partnership your
                    agents are on, the chain carries {inr(pending.limitRupees - adminCarries)} of it and{' '}
                    <b>you carry {inr(adminCarries)}</b> — a house loss books down the chain exactly as it
                    always has.
                  </>
                ) : (
                  <>
                    Part of it is carried by the agent chain — a house loss books down the chain exactly
                    as it always has — but this card cannot say how much: the partnership share is not in
                    the data it was given, so the figure above is the whole amount, before the chain&rsquo;s
                    share is taken off.
                  </>
                )}
              </p>
              <p className="text-[12px] text-arena-500 dark:text-neon-200/60">
                Worst case, statable in advance and it does not grow with turnover, players or time:{' '}
                {inr(pending.limitRupees)} plus one capped payout ({inr(spec.maxSinglePayoutRupees)}) ={' '}
                {inr(pending.limitRupees + spec.maxSinglePayoutRupees)}.
              </p>
            </section>
          )}

          {/* ── 5. A6 — the ceiling the dial can actually reach ───────────────────────────── */}
          {ceiling != null && (
            <div className={`rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed
                            ${ceilingBreached
                              ? 'bg-amber-50 border-amber-300/70 text-amber-900 dark:bg-amber-500/10 dark:border-amber-400/30 dark:text-amber-100'
                              : 'bg-arena-50 border-arena-200/70 text-arena-800 dark:bg-arena-700/40 dark:border-arena-600/50 dark:text-neon-100'}`}>
              {ceilingBreached ? (
                <>
                  <b>⚠ This game cannot deliver the target it is set to.</b> {gameLabel(game)} is dialled
                  at {targetPct}% but tops out at about <b>{ceiling}%</b> on{' '}
                  {spec.takeCeilingBoard ?? spec.board} — measured, by driving the real engine at
                  ascending dials. Above the ceiling the game quietly delivers less than the number on
                  the slider. Lower the target, or change that board.
                </>
              ) : (
                <>
                  Highest target this game can actually deliver: <b>{ceiling}%</b> — measured by driving
                  the real engine at ascending dials on {spec.takeCeilingBoard ?? spec.board}, the board
                  where it runs out of room first.{' '}
                  {targetPct != null ? `It is dialled at ${targetPct}%, inside that.` : ''}
                </>
              )}
            </div>
          )}

          {/* ── 6. the bankroll the owner must set ────────────────────────────────────────── */}
          <section className="rounded-2xl bg-white dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-4 sm:p-5 space-y-2.5">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <h2 className="text-[14px] font-bold text-arena-900 dark:text-white">House bankroll</h2>
              <span className="text-[11px] font-semibold text-arena-500 dark:text-neon-200/70">
                Now:{' '}
                <span className={bankrollSet ? 'text-arena-800 dark:text-neon-100' : 'text-amber-600 dark:text-amber-300'}>
                  {bankrollSet ? inr(num(data?.bankroll?.rupees)!) : `not set — running on a ${inr(bankrollDefault)} placeholder`}
                </span>
              </span>
            </div>
            <p className="text-[12.5px] leading-relaxed text-arena-600 dark:text-neon-200/75">
              One number, and only you can answer it: <b>how much of your own money are you willing to
              have on the table across all games at once?</b> It is not lifetime profit, not the balance
              of any wallet, and not what players have deposited — that is theirs.
            </p>

            {/* ── WHAT THIS FIGURE MEANS FOR THE SELECTED GAME (2026-09-16) ─────────────────
                Asked for directly: "which value would do what and what is needed, what is not
                — add that text in each game differently as each game is different."
                They differ by 85x: Thimbles' top prize costs ₹2,940 to be able to pay and Cash
                Casino's costs ₹2,50,000, because the funding guard charges the FULL payout
                against capacity. One generic sentence would be wrong on seven of the eight. */}
            {(() => {
              const v = bankrollVerdictFor(game, bankrollSet ? num(data?.bankroll?.rupees) : null);
              if (!v) return null;
              const tone = !bankrollSet
                ? 'bg-amber-50 border-amber-300/70 text-amber-900 dark:bg-amber-500/10 dark:border-amber-400/30 dark:text-amber-100'
                : v.covered
                  ? 'bg-emerald-50 border-emerald-300/70 text-emerald-900 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-100'
                  : 'bg-rose-50 border-rose-300/70 text-rose-900 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-100';
              return (
                <div className={`rounded-xl border px-3 py-2.5 text-[12.5px] leading-relaxed space-y-1.5 ${tone}`}>
                  <div className="font-bold flex items-center gap-1.5">
                    <Icon name={v.covered && bankrollSet ? 'check_circle' : 'info'} size={16} />
                    {gameLabel(game)} needs {inr(v.needRupees)} to pay its top prize
                  </div>
                  <p>{v.character}</p>
                  <p>{v.verdict}</p>
                </div>
              );
            })()}
            {/* WHEN IT TAKES EFFECT, said plainly. The payout precheck is armed per game by that
                game's own reserve switch (`const houseSolvency = reserveOn ? … : null` in the plinko
                controller and in the roulette / casino models), so while every switch is off this
                figure is read by nothing. Saying "every game checks a payout against it" would be
                exactly the kind of claim this project calls a dead control. */}
            <p className="text-[12.5px] leading-relaxed text-arena-600 dark:text-neon-200/75">
              It starts being read the moment a game&rsquo;s reserve is switched on, and not before —
              which is the whole reason to set it first. From then on that game refuses any payout its
              share of this figure cannot fund. Leave it unset and the game falls back to a{' '}
              {inr(bankrollDefault)} placeholder, and will turn down payouts it should have allowed.
            </p>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-bold text-arena-500 dark:text-neon-200/70">₹</span>
              <input
                type="number"
                min={0}
                step={1000}
                inputMode="numeric"
                disabled={!wired || saving}
                value={bankroll}
                onChange={(e) => setBankroll(e.target.value)}
                placeholder={String(bankrollDefault)}
                className="w-full max-w-[220px] rounded-xl px-3 py-2 text-[14px] bg-white dark:bg-arena-700/40
                           border border-arena-200/60 dark:border-arena-600/50 text-arena-900 dark:text-white
                           placeholder-arena-400 dark:placeholder-neon-200/55 focus:outline-none
                           focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 disabled:opacity-50"
              />
            </div>
            {num(data?.bankroll?.capacityRupees) != null && (
              <p className="text-[12px] text-arena-500 dark:text-neon-200/65">
                On today&rsquo;s reading a game with its reserve on could fund{' '}
                {inr(num(data!.bankroll!.capacityRupees)!)} of payouts — your figure, less anything the
                house has lost since you set it, less money already promised to rounds still open.
              </p>
            )}
            <p className="text-[11.5px] text-arena-500 dark:text-neon-200/60">
              Saving stamps today&rsquo;s house P&amp;L reading alongside it, so the figure can fall as the
              house loses without anyone having to re-type it. Raising it again is your decision alone.
            </p>
          </section>

          {/* ── 7. ONE Save ──────────────────────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => void save()}
              disabled={!wired || saving || !dirty.any || !azValid}
              className="px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50
                         text-white text-sm font-bold shadow-[0_2px_10px_rgba(139,92,246,0.35)]
                         transition active:scale-95"
            >
              {saving ? 'Saving…' : 'Save reserve settings'}
            </button>
            {!azValid && (
              <span className="text-[13px] text-amber-600 dark:text-amber-400">
                Authority must be between {AUTHORITY_MIN} and {AUTHORITY_MAX}.
              </span>
            )}
            {wired && !dirty.any && !msg && (
              <span className="text-[12.5px] text-arena-500 dark:text-neon-200/60">Nothing changed yet.</span>
            )}
            {msg && <span className="text-[13px] text-emerald-600 dark:text-emerald-400">{msg}</span>}
            {err && <span className="text-[13px] text-rose-600 dark:text-rose-400">{err}</span>}
          </div>

          <p className="text-[11px] leading-snug text-arena-400 dark:text-neon-200/45">
            Every figure in the explanations above was measured on the real game engines — the sizing
            with migration 168&rsquo;s sweep, the six-loss ladder and the ceiling with their own runs — and
            re-measured whenever a multiplier table, a bet limit or a payout cap changes.
          </p>
        </>
      )}
    </div>
  );
}
